import Direction3D from './Direction3D';
import Line3D from './Line3D';
import LineSegment2D from './LineSegment2D';
import Plane3D from './Plane3D';
import Point3D from './Point3D';
import { centerPoint3D } from './Polygon3D';
import { distanceEquals } from './units';
import Vector3D from './Vector3D';

class LineSegment3D {
  readonly basePoint: Point3D;
  readonly endPoint: Point3D;

  constructor(basePoint: Point3D, endPoint: Point3D) {
    this.basePoint = basePoint;
    this.endPoint = endPoint;
    if (this.basePoint.equals(this.endPoint)) throw new Error('LineSegment has no breadth!');
  }

  get endPoints() {
    return [this.basePoint, this.endPoint];
  }

  get length() {
    return this.basePoint.distanceToPoint(this.endPoint);
  }

  get direction() {
    return Direction3D.fromPointToPoint(this.basePoint, this.endPoint)!;
  }

  get midpoint() {
    return centerPoint3D(this.endPoints);
  }

  reverse() {
    return new LineSegment3D(this.endPoint, this.basePoint);
  }

  orientWith(direction: Direction3D): LineSegment3D {
    const dotProductIsNegative = this.direction.dotProduct(direction) < 0;
    return dotProductIsNegative ? this.reverse() : this;
  }

  isParallelTo(line: LineSegment3D) {
    return this.direction.isParallelTo(line.direction);
  }

  isPerpendicularTo(line: LineSegment3D) {
    return this.direction.isPerpendicularTo(line.direction);
  }

  contains(point: Point3D) {
    return this.toLine().contains(point) && this.containsPointKnownToBeOnLine(point);
  }

  containsPointKnownToBeOnLine(point: Point3D) {
    return distanceEquals(point.distanceToPoint(this.basePoint) + point.distanceToPoint(this.endPoint), this.length);
  }

  containsOnInside(point: Point3D) {
    return this.endPoints.every((endPoint) => !endPoint.equals(point)) && this.contains(point);
  }

  equals(segment: LineSegment3D) {
    return (
      (this.basePoint.equals(segment.basePoint) && this.endPoint.equals(segment.endPoint)) ||
      (this.basePoint.equals(segment.endPoint) && this.endPoint.equals(segment.basePoint))
    );
  }

  insideIntersectionPoint(segment: LineSegment3D): Point3D | null {
    const lineIntersection = this.toLine().intersectWithLine(segment.toLine());
    return lineIntersection instanceof Point3D &&
      this.containsOnInside(lineIntersection) &&
      segment.containsOnInside(lineIntersection)
      ? lineIntersection
      : null;
  }

  overlaps(segment: LineSegment3D) {
    return (
      this.equals(segment) ||
      (this.isParallelTo(segment) &&
        (!this.endPoints.every((point) => !segment.containsOnInside(point)) ||
          !segment.endPoints.every((point) => !this.containsOnInside(point))))
    );
  }

  projectTo2D(): LineSegment2D | null {
    const [basePoint, endPoint] = [this.basePoint.projectTo2D(), this.endPoint.projectTo2D()];
    if (basePoint.equals(endPoint)) return null;
    return new LineSegment2D(basePoint, endPoint);
  }

  projectOntoPlane(plane: Plane3D): LineSegment3D | null {
    const newBasePoint = this.basePoint.projectOntoPlane(plane);
    const newEndPoint = this.endPoint.projectOntoPlane(plane);
    return newBasePoint.equals(newEndPoint) ? null : new LineSegment3D(newBasePoint, newEndPoint);
  }

  translate(vector: Vector3D) {
    return new LineSegment3D(this.basePoint.translate(vector), this.endPoint.translate(vector));
  }

  toLine() {
    return new Line3D(this.direction, this.basePoint);
  }
}

export default LineSegment3D;
